import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, defaultLandingForRole } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import { Anchor, ArrowRight, Mail, Lock, Eye, EyeOff } from "lucide-react";

import lficon from "../assets/LF_icon.png";
import lfloginbg from "../assets/loginpage_bg_2.png";


export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome, ${user.name || user.email}`);
      if (user.must_change_password) {
        navigate("/change-password", { replace: true });
      } else {
        navigate(defaultLandingForRole(user.role), { replace: true });
      }
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FAFCFC]">
      {/* Left visual */}
      <div className="hidden lg:flex relative items-end p-12 text-white overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              `url(${lfloginbg})`
          }}
        />
        <div className="absolute inset-0 bg-black/65" />
        <div className="relative z-10 max-w-md space-y-6">
          <div className="flex items-center gap-3">
            <span className="h-10 w-10 grid place-items-center bg-[#2252b5] rounded-sm">
              <Anchor className="h-5 w-5 text-black" strokeWidth={2.5} />
            </span>
            <span className="font-heading font-black text-xl tracking-tight">LFSOFTECH HRMS</span>

          </div>
          <h1 className="font-heading text-5xl font-black tracking-tight leading-[1.05]">
            People &amp; freight, <br />
            <span className="whitespace-nowrap">one control platform.</span>
          </h1>
          <p className="text-zinc-300 text-sm leading-relaxed">
            Multi-tenant HRMS for shipping &amp; logistics. Manage crew, attendance, payroll and shipping invoices for your entire workflows from one centralized platform..
          </p>
          <div className="flex gap-2 pt-4">
            <span className="font-mono text-[10px] uppercase tracking-widest px-3 py-1 border border-zinc-600 text-zinc-300">
              Crew Manifest
            </span>
            <span className="font-mono text-[10px] uppercase tracking-widest px-3 py-1 border border-zinc-600 text-zinc-300">
              Freight Invoicing
            </span>
            <span className="font-mono text-[10px] uppercase tracking-widest px-3 py-1 border border-zinc-600 text-zinc-300">
              Reports & Analytics
            </span>
          </div>
        </div>
      </div>

            {/* Right form */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">

          {/* Mobile logo position */}
          <div className="w-full flex justify-center items-center mb-1 lg:-translate-x-8">
            <img
              src={lficon}
              alt="LFSoftech HRMS"
              className="h-40 lg:h-32 w-auto object-contain"
            />
          </div>
          <div className="font-mono text-[11px] tracking-widest text-zinc-500 uppercase -mt-3 mb-2">
            Authentication
          </div>
          <h2 className="font-heading text-3xl font-black tracking-tight mb-1">
            Sign in to your workspace
          </h2>
          <p className="text-sm text-zinc-500 mb-8">
            Use your work email to access the control room.
          </p>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[10px] uppercase tracking-widest font-mono text-zinc-500">
                Email
              </Label>

              <div className="relative">
                <Mail
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
                  size={15}
                />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email"
                  className="rounded-sm h-11 border-zinc-300 pl-10 translate-y-[-2px]"
                  placeholder="you@example.com"
                />
              </div>

            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-[10px] uppercase tracking-widest font-mono text-zinc-500">
                Password
              </Label>

              <div className="relative">

                <Lock
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
                  size={15}
                />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password"
                  placeholder="password"
                  className="rounded-sm h-11 border-zinc-300 pl-10 pr-10 translate-y-[-2px] text-sm"
                />

                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500"
                >

                  {showPassword ? (
                    <Eye size={18} />
                  ) : (
                    <EyeOff size={18} />
                  )}

                </button>
              </div>

            </div>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="login-submit"
              className="btn-pressable w-full h-11 rounded-sm bg-[#F97316] hover:bg-[#EA580C] text-white font-mono uppercase tracking-widest text-xs"
            >
              {submitting ? "Signing in…" : (
                <span className="flex items-center gap-2">
                  Sign in <ArrowRight className="h-4 w-4" />
                </span>
              )}
            </Button>
          </form>

          <div className="mt-8 pt-6 border-t border-zinc-200 text-xs text-zinc-500">
            New user?{" "}
            <Link to="/register" className="text-[#09090B] font-medium underline-offset-4 hover:underline" data-testid="goto-register">
              Create an account
            </Link>
          </div>

          {/* <div className="mt-6 p-3 bg-zinc-100 border border-zinc-200 rounded-sm">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
              Demo credentials
            </div>
            <div className="font-mono text-xs text-zinc-700">
              admin@oceanfreight.com · admin123
            </div>
          </div> */}

          <div className="mt-24 lg:mt-16 flex items-center justify-center gap-0 text-[13.5px] text-zinc-500 -translate-x-2 translate-y-6 lg:translate-y-0">
            <span>© 2026 Powered by</span>

            <img
              src={lficon}
              alt="LF Softech"
              className="h-6 w-auto object-contain ml-[-5px] mr-0 text-[5px]"
            />

            <span className="font-semibold tracking-wide text-zinc-600 text-[14px] -ml-2">
              SOFTECH
            </span>
          </div>

        </div>
      </div>
    </div>
  );
}
